import { useEffect, useState } from 'react';
import { api, ContributionRank, LeaderboardOptIn, PerformanceRank } from '../api';
import { Icon } from '../Icon';
import { useSession } from '../auth/session';

/**
 * Two boards, two separate choices.
 *
 * Contribution standing and practice performance are different kinds of
 * exposure. One says "this person helped the bank"; the other publishes how
 * well someone is doing on a study tool, which is the sort of thing that
 * discourages exactly the people who most need the practice. Opting into one
 * must never opt someone into the other, so they are stored and asked
 * separately, and both are off until chosen.
 *
 * Nobody appears without opting in, and only a display name is ever shown.
 */
export function Leaderboards({ onBack }: { onBack: () => void }) {
  const { learner } = useSession();
  const [optIn, setOptIn] = useState<LeaderboardOptIn>({ contributions: false, performance: false });
  const [contrib, setContrib] = useState<ContributionRank[]>([]);
  const [perf, setPerf] = useState<PerformanceRank[]>([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get<LeaderboardOptIn>('/me/leaderboard').then(setOptIn).catch(() => {});
    api.get<ContributionRank[]>('/leaderboard/contributions').then(setContrib).catch(() => {});
    api.get<PerformanceRank[]>('/leaderboard/performance').then(setPerf).catch(() => {});
  };
  useEffect(load, []);

  async function change(next: Partial<LeaderboardOptIn>) {
    const merged = { ...optIn, ...next };
    setOptIn(merged); setSaving(true);
    try { await api.put('/me/leaderboard', merged); load(); }
    finally { setSaving(false); }
  }

  const me = learner?.displayName ?? learner?.email ?? '';

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back</button>
      <h1>Leaderboards</h1>

      <fieldset>
        <legend>Do you want to take part?</legend>
        <p className="muted" style={{ marginTop: 0 }}>
          Both are off unless you choose them, and each is a separate choice. Only your display name
          is shown — never your email address. You can leave at any time and you disappear from the
          board immediately.
        </p>

        <label className="choice" style={{ marginBottom: '0.5rem' }}>
          <input type="checkbox" checked={optIn.contributions} disabled={saving}
                 onChange={(e) => void change({ contributions: e.target.checked })} />
          <span>
            <strong>Contributions board</strong>
            <br />
            <span className="muted">
              Shows your cookies — accepted question reports and published questions you wrote.
            </span>
          </span>
        </label>

        <label className="choice">
          <input type="checkbox" checked={optIn.performance} disabled={saving}
                 onChange={(e) => void change({ performance: e.target.checked })} />
          <span>
            <strong>Practice performance board</strong>
            <br />
            <span className="muted">
              Shows how much of your practice you answer correctly. This makes your results visible
              to other signed-in learners.
            </span>
          </span>
        </label>
      </fieldset>

      <h2>Contributions</h2>
      {contrib.length === 0 ? (
        <p className="muted">Nobody has opted in and earned a cookie yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="board">
            <thead>
              <tr>
                <th scope="col">#</th><th scope="col">Name</th><th scope="col">Cookies</th>
                <th scope="col">Reports</th><th scope="col">Questions</th>
              </tr>
            </thead>
            <tbody>
              {contrib.map((r) => (
                <tr key={r.rank} className={r.name === me ? 'is-me' : undefined}>
                  <td>{r.rank}</td>
                  <td>{r.name}{r.name === me && <span className="muted"> (you)</span>}</td>
                  <td>{r.points}</td><td>{r.disputes}</td><td>{r.questions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Practice performance</h2>
      {perf.length === 0 ? (
        <p className="muted">
          Nobody has opted in with enough answered questions yet. A minimum of 50 scored questions is
          needed, so that one lucky short session cannot top the board.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="board">
            <thead>
              <tr>
                <th scope="col">#</th><th scope="col">Name</th><th scope="col">Correct</th>
                <th scope="col">Scored</th><th scope="col">Sessions</th>
              </tr>
            </thead>
            <tbody>
              {perf.map((r) => (
                <tr key={r.rank} className={r.name === me ? 'is-me' : undefined}>
                  <td>{r.rank}</td>
                  <td>{r.name}{r.name === me && <span className="muted"> (you)</span>}</td>
                  <td>{r.accuracy}%</td><td>{r.scored}</td><td>{r.attempts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted">
        These are raw practice figures. They are not scaled scores and they do not predict the
        outcome of any real examination.
      </p>
    </>
  );
}
